import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Scissors, Users, UserCircle2, CalendarDays, BarChart3, LogOut, Menu, ExternalLink, Settings as SettingsIcon, ShieldCheck, Share2, Building2, Ban, Receipt } from "lucide-react";
import { api, setToken } from "@/lib/api";
import { applyBrandColor, rememberLastTenantSlug } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { can, useMe } from "@/lib/permissions";
import NotifBell from "@/components/NotifBell";
import { isImpersonating, impersonatingName, stopImpersonation } from "@/lib/impersonation";

const items = [
  { to: "/", label: "Painel", icon: LayoutDashboard, end: true, tid: "nav-dashboard" },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, tid: "nav-agenda" },
  { to: "/servicos", label: "Serviços", icon: Scissors, tid: "nav-servicos", hideForPro: true },
  { to: "/profissionais", label: "Profissionais", icon: UserCircle2, tid: "nav-profissionais", hideForPro: true },
  { to: "/bloqueios", label: "Bloqueios", icon: Ban, tid: "nav-bloqueios", perm: "professionals.write" },
  { to: "/clientes", label: "Clientes", icon: Users, tid: "nav-clientes", hideForPro: true },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, tid: "nav-relatorios", perm: "reports.view", proPerm: "reports.view_commission" },
  { to: "/configuracoes", label: "Configurações", icon: SettingsIcon, tid: "nav-configuracoes", perm: "settings.write", hideForPro: true },
  { to: "/usuarios", label: "Usuários", icon: ShieldCheck, tid: "nav-usuarios", perm: "users.manage", hideForPro: true },
  { to: "/franquias", label: "Franquias", icon: Building2, tid: "nav-franquias", platformOnly: true },
  { to: "/faturas", label: "Faturas", icon: Receipt, tid: "nav-faturas", ownerOnly: true },
];

export default function AppLayout() {
  const nav = useNavigate();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [brand, setBrand] = useState({ business_name: "", logo_url: "", tagline: "" });

  useEffect(() => {
    try { setUser(JSON.parse(localStorage.getItem("user") || "null")); } catch {}
    // Refresh me() to pick up permissions changes
    api.get("/auth/me").then(({ data }) => {
      localStorage.setItem("user", JSON.stringify(data));
      setUser(data);
      rememberLastTenantSlug(data?.tenant_slug);
    }).catch(() => {});
    api.get("/settings").then(({ data }) => {
      setBrand({
        business_name: data.business_name || "",
        logo_url: data.logo_url || "",
        tagline: data.tagline || "",
      });
      applyBrandColor(data.brand_color || "");
    }).catch(() => {});
  }, []);

  const isProLogin = !!me?.professional_id;
  const isPlatformAdmin = !!me?.is_platform_admin || me?.role === "platform_admin";
  const isOwnerNotPlatform = me?.role === "owner" && !isPlatformAdmin;
  const filteredItems = items.filter((it) => {
    if (it.platformOnly) return isPlatformAdmin;
    if (it.ownerOnly) return isOwnerNotPlatform;
    if (isProLogin) {
      if (it.hideForPro) return false;
      if (it.proPerm) return (me?.permissions || []).includes(it.proPerm);
      return true;
    }
    if (it.perm) return can(it.perm) || (me?.role === "owner");
    return true;
  });

  const logout = () => {
    if (isImpersonating()) { stopImpersonation(); return; }
    setToken(null); localStorage.removeItem("user"); nav("/login");
  };
  const impersonating = isImpersonating();

  const NavItems = ({ onClick }) => (
    <nav className="flex flex-col gap-1">
      {filteredItems.map(({ to, label, icon: Icon, end, tid }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          data-testid={tid}
          onClick={onClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200
             ${isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"}`
          }
        >
          <Icon className="h-4 w-4" /> {label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex md:w-64 lg:w-72 shrink-0 flex-col border-r border-border bg-card p-6 gap-6">
        <div className="flex items-center gap-3">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt="Logo" className="h-11 w-11 rounded-lg object-cover border border-border" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="h-11 w-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-display font-bold">A</div>
          )}
          <div className="min-w-0">
            <div className="text-lg font-display font-bold tracking-tight truncate">{brand.business_name || <>Agenda<span className="text-accent">.</span></>}</div>
            <div className="text-xs text-muted-foreground truncate">{brand.tagline || "Painel do estabelecimento"}</div>
          </div>
        </div>
        <NavItems />
        <div className="mt-auto space-y-2">
          <a
            href={user?.tenant_slug ? `/agendar/${user.tenant_slug}` : "/agendar"}
            target="_blank"
            rel="noreferrer"
            data-testid="link-public-booking"
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Página pública de agendamento
          </a>
          <div className="pt-3 border-t border-border">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
              </div>
              <NotifBell enabled={isProLogin} />
            </div>
            <Button data-testid="btn-logout" variant="ghost" size="sm" className="mt-2 w-full justify-start" onClick={logout}>
              <LogOut className="h-4 w-4 mr-2" /> Sair
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between border-b border-border bg-card/80 backdrop-blur-xl px-4 h-14 sticky top-0 z-30">
          <div className="flex items-center gap-2 min-w-0">
            {brand.logo_url && <img src={brand.logo_url} alt="" className="h-8 w-8 rounded object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            <div className="text-lg font-display font-bold tracking-tight truncate">{brand.business_name || <>Agenda<span className="text-accent">.</span></>}</div>
          </div>
          <div className="flex items-center gap-2">
            <NotifBell enabled={isProLogin} />
            <Button data-testid="btn-mobile-menu" variant="ghost" size="icon" onClick={() => setOpen(!open)}>
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </header>
        {open && (
          <div className="md:hidden border-b border-border bg-card p-4">
            <NavItems onClick={() => setOpen(false)} />
            <Button data-testid="btn-logout-mobile" variant="ghost" size="sm" className="mt-4 w-full justify-start" onClick={logout}>
              <LogOut className="h-4 w-4 mr-2" /> Sair
            </Button>
          </div>
        )}
        {impersonating && (
          <div className="sticky top-14 md:top-0 z-20 bg-amber-500 text-amber-950 px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid="impersonation-banner">
            <span>Você está acessando como <strong>{impersonatingName() || user?.name}</strong>. As alterações são feitas na conta da franquia.</span>
            <Button size="sm" variant="secondary" onClick={stopImpersonation} data-testid="btn-stop-impersonation">Voltar ao administrador</Button>
          </div>
        )}
        <main className="flex-1 p-4 sm:p-6 lg:p-10 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
